import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  useCreateInvestigator,
  useInvestigator,
  useUpdateInvestigator,
} from "@/api/hooks/investigators";
import {
  DEBARMENT_STATUSES,
  DEBARMENT_STATUS_LABELS,
} from "@/api/labels";
import type { DebarmentStatus } from "@/api/types";

interface FormState {
  first_name: string;
  last_name: string;
  npi: string;
  email: string;
  phone: string;
  gcp_training_expires_at: string;
  hipaa_training_expires_at: string;
  debarment_status: DebarmentStatus;
  debarment_checked_at: string;
  board_certifications: string;
  notes: string;
  is_active: boolean;
}

const EMPTY: FormState = {
  first_name: "",
  last_name: "",
  npi: "",
  email: "",
  phone: "",
  gcp_training_expires_at: "",
  hipaa_training_expires_at: "",
  debarment_status: "not_checked",
  debarment_checked_at: "",
  board_certifications: "",
  notes: "",
  is_active: true,
};

export default function InvestigatorForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: existing, isLoading } = useInvestigator(id);
  const create = useCreateInvestigator();
  const update = useUpdateInvestigator(id ?? "");

  const [form, setForm] = useState<FormState>(EMPTY);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setForm({
        first_name: existing.first_name,
        last_name: existing.last_name,
        npi: existing.npi ?? "",
        email: existing.email ?? "",
        phone: existing.phone ?? "",
        gcp_training_expires_at: existing.gcp_training_expires_at ?? "",
        hipaa_training_expires_at: existing.hipaa_training_expires_at ?? "",
        debarment_status: existing.debarment_status,
        debarment_checked_at: existing.debarment_checked_at
          ? existing.debarment_checked_at.slice(0, 10)
          : "",
        board_certifications: existing.board_certifications ?? "",
        notes: existing.notes ?? "",
        is_active: existing.is_active,
      });
    }
  }, [existing]);

  if (isEdit && isLoading) return <CircularProgress />;

  // Detect compliance-significant changes (only meaningful in edit mode).
  const debarmentChanged =
    isEdit &&
    existing != null &&
    form.debarment_status !== existing.debarment_status;
  const activationChanged =
    isEdit && existing != null && form.is_active !== existing.is_active;
  const reasonRequired = debarmentChanged || activationChanged;

  const submit = async () => {
    setError(null);
    if (reasonRequired && reason.trim().length < 5) {
      setError("Please provide a reason (≥ 5 characters) for this change.");
      return;
    }
    try {
      if (isEdit) {
        await update.mutateAsync({
          first_name: form.first_name.trim() || null,
          last_name: form.last_name.trim() || null,
          npi: form.npi.trim() || null,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          gcp_training_expires_at: form.gcp_training_expires_at || null,
          hipaa_training_expires_at: form.hipaa_training_expires_at || null,
          debarment_status: form.debarment_status,
          debarment_checked_at: form.debarment_checked_at
            ? `${form.debarment_checked_at}T00:00:00Z`
            : null,
          board_certifications: form.board_certifications.trim() || null,
          notes: form.notes.trim() || null,
          is_active: form.is_active,
          reason: reasonRequired ? reason.trim() : null,
        });
      } else {
        await create.mutateAsync({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          npi: form.npi.trim() || null,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          gcp_training_expires_at: form.gcp_training_expires_at || null,
          hipaa_training_expires_at: form.hipaa_training_expires_at || null,
          debarment_status: form.debarment_status,
          debarment_checked_at: form.debarment_checked_at
            ? `${form.debarment_checked_at}T00:00:00Z`
            : null,
          board_certifications: form.board_certifications.trim() || null,
          notes: form.notes.trim() || null,
        });
      }
      navigate("/clinops/sites/investigators");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEdit ? "Edit investigator" : "New investigator"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="First name"
            value={form.first_name}
            onChange={(e) =>
              setForm((f) => ({ ...f, first_name: e.target.value }))
            }
            required
            fullWidth
          />
          <TextField
            label="Last name"
            value={form.last_name}
            onChange={(e) =>
              setForm((f) => ({ ...f, last_name: e.target.value }))
            }
            required
            fullWidth
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="NPI (10 digits)"
            value={form.npi}
            onChange={(e) => setForm((f) => ({ ...f, npi: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={(e) =>
              setForm((f) => ({ ...f, email: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(e) =>
              setForm((f) => ({ ...f, phone: e.target.value }))
            }
            fullWidth
          />
        </Stack>

        <Typography variant="overline" color="text.secondary">
          Credentialing
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            type="date"
            label="GCP training expires"
            value={form.gcp_training_expires_at}
            onChange={(e) =>
              setForm((f) => ({ ...f, gcp_training_expires_at: e.target.value }))
            }
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <TextField
            type="date"
            label="HIPAA training expires"
            value={form.hipaa_training_expires_at}
            onChange={(e) =>
              setForm((f) => ({ ...f, hipaa_training_expires_at: e.target.value }))
            }
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            select
            label="Debarment status"
            value={form.debarment_status}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                debarment_status: e.target.value as DebarmentStatus,
              }))
            }
            fullWidth
          >
            {DEBARMENT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {DEBARMENT_STATUS_LABELS[s]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label="Debarment checked at"
            value={form.debarment_checked_at}
            onChange={(e) =>
              setForm((f) => ({ ...f, debarment_checked_at: e.target.value }))
            }
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        </Stack>

        <TextField
          label="Board certifications"
          value={form.board_certifications}
          onChange={(e) =>
            setForm((f) => ({ ...f, board_certifications: e.target.value }))
          }
          multiline
          rows={2}
          helperText="Free-text in Phase 1 (e.g., 'ABIM Cardiology — exp 2027')"
        />

        <TextField
          label="Notes"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          multiline
          rows={2}
        />

        {isEdit && (
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) =>
                  setForm((f) => ({ ...f, is_active: e.target.checked }))
                }
              />
            }
            label={form.is_active ? "Active" : "Inactive"}
          />
        )}

        {reasonRequired && (
          <Alert severity="info">
            <Typography variant="body2" sx={{ mb: 1 }}>
              {debarmentChanged && activationChanged
                ? "Debarment status and active status are both changing — these are CFR Part 11 events."
                : debarmentChanged
                  ? "Debarment status is changing — this is a CFR Part 11 event."
                  : "Active status is changing — this is a CFR Part 11 event."}
            </Typography>
            <TextField
              label="Reason for change"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              required
              helperText="Required (≥ 5 characters) — recorded on the audit log."
            />
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={() => navigate("/clinops/sites/investigators")}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={!form.first_name.trim() || !form.last_name.trim()}
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
